import { useCallback } from 'react'
import { Button } from 'blaze-ui/Button'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Upload5 } from 'blaze-ui/icons/20'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { FileTrigger } from 'components/FileTrigger/FileTrigger'
import {
  UPLOAD_AUDIO_MIME_TYPES,
  UPLOAD_DOCUMENT_MIME_TYPES,
  UPLOAD_IMAGE_MIME_TYPES,
  UPLOAD_VIDEO_MIME_TYPES,
} from 'screens/Finder/components/views/GalleryView/constants'
import { useCreateMediaObjectsWithProgress } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import { CampaignSourceObject } from './Types'
import { pluralize } from 'utils'

export const DEFAULT_ACCEPT = [...UPLOAD_DOCUMENT_MIME_TYPES].join(',')
export const ACCEPT_WITH_MEDIA = [
  ...UPLOAD_DOCUMENT_MIME_TYPES,
  ...UPLOAD_AUDIO_MIME_TYPES,
  ...UPLOAD_VIDEO_MIME_TYPES,
  ...UPLOAD_IMAGE_MIME_TYPES,
].join(',')

export interface UploadButtonProps extends Omit<ButtonProps, 'onPress'> {
  onSourceCreated: (source: CampaignSourceObject) => Promise<any> | void
  acceptMedia?: boolean
  useToastLoading?: boolean
  onUploadStart?: () => void
  onUploadEnd?: () => void
}

export const UploadButton = ({
  variant = 'secondary',
  onSourceCreated,
  acceptMedia = false,
  useToastLoading = false,
  onUploadStart,
  onUploadEnd,
  children = ' Upload File',
  ...buttonProps
}: UploadButtonProps) => {
  const { mutateAsync: createMediaObjects } = useCreateMediaObjectsWithProgress({ onProgress: () => {} })
  const { mutateAsync: createSource } = useCreateSource()

  const handleFiles = useCallback(
    async (files: FileList) => {
      onUploadStart?.()
      const dismiss = showToastMessage(`Uploading ${pluralize('file', files.length)}...`, 'loading', undefined, 0)
      try {
        const mediaObjects = await createMediaObjects(Array.from(files).map((file) => ({ file, filename: file.name })))
        await Promise.all(
          mediaObjects.map(async (mediaObject) => {
            const result = await createSource({ media_object_id: mediaObject.id, type: 'SourceMaterialsMediaObject' })
            onSourceCreated(result.source)
          }),
        )
        dismiss()
        showToastMessage(`${pluralize('File', files.length)} uploaded`, 'success', undefined, 3)
      } catch {
        dismiss()
        showToastMessage(
          `There was a problem uploading your ${pluralize('file', files.length)}. Try again.`,
          'error',
          undefined,
          5,
        )
      } finally {
        onUploadEnd?.()
      }
    },
    [createMediaObjects, createSource, onSourceCreated, onUploadStart, onUploadEnd],
  )

  return (
    <FileTrigger
      onSelect={(files) => {
        if (!files) return

        if (files[0].size > 1024 * 1024 * 1024) {
          showErrorMessageToast(
            'Your file exceeds our 1 GB limit. Please upload a smaller file, or compress your current one.',
          )
          return
        }

        handleFiles(files)
      }}
      accept={acceptMedia ? ACCEPT_WITH_MEDIA : DEFAULT_ACCEPT}
      allowsMultiple
      aria-label="Upload Campaign Source"
    >
      <Button variant={variant} frontIcon={Upload5} {...buttonProps}>
        {children}
      </Button>
    </FileTrigger>
  )
}
